import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section } from "../components/Section";
import { CTA } from "../components/CTA";
import { useIdentity } from "../lib/identity";
import {
  lookupCompanyByEin,
  registerNewCompany,
  requestCompanyJoin,
  completeCompanyJoin,
  unifiedRegister,
} from "../lib/unified-auth";
import type { CompanyLookupResult } from "../lib/unified-auth";
import { useTranslation } from "../lib/i18n";
import { Eye, EyeOff, Building2, User, Upload } from "lucide-react";
import { sendEmail, buildRegistrationConfirmationEmail } from "../lib/email-helper";
import { supabase } from "../lib/supabase";
import { INDUSTRIES } from "../lib/industries";
import { updateOrgIndustryPreferences, updateUserIndustryPreferences } from "../lib/portal-api";

type Step = "choose" | "ein" | "company-found" | "company-new" | "join-code" | "personal" | "individual";
type AccountType = "individual" | "company";

const COMPANY_TYPES = [
  { value: "law_enforcement", label: "Law Enforcement" },
  { value: "healthcare", label: "Healthcare" },
  { value: "education", label: "Education" },
  { value: "hospitality", label: "Hospitality" },
  { value: "culinary", label: "Culinary" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "transportation", label: "Transportation" },
  { value: "defense", label: "Defense" },
  { value: "municipal", label: "Municipal" },
  { value: "nonprofit", label: "Nonprofit" },
];

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIdentity } = useIdentity();

  const [step, setStep] = useState<Step>("choose");
  const [accountType, setAccountType] = useState<AccountType>("individual");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [companyLookup, setCompanyLookup] = useState<CompanyLookupResult | null>(null);
  const [joinCodeSent, setJoinCodeSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [form, setForm] = useState({
    ein: "",
    companyName: "",
    companyType: "law_enforcement",
    departmentName: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    verificationCode: "",
    isNewDepartment: false,
    existingDepartmentName: "",
    industry: "",
  });
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);

  const update = (field: string, value: string | boolean) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  // ── Individual registration ──
  const handleIndividualRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }
    if (!form.email.trim() || !form.password) {
      setError("Email and password are required.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setStatus("submitting");
    setError(null);
    const { identity, error: err } = await unifiedRegister({
      email: form.email.trim(),
      password: form.password,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
    });
    if (err || !identity) {
      setError(err ?? "Registration failed.");
      setStatus("error");
      return;
    }
    setIdentity(identity);

    // Save industry preferences for individual user
    if (selectedIndustries.length > 0 && identity.userId) {
      await updateUserIndustryPreferences(identity.userId, selectedIndustries);
    }

    setStatus("success");

    // Send confirmation email
    const { subject, body } = buildRegistrationConfirmationEmail(
      `${form.firstName} ${form.lastName}`.trim(),
      "Sentinel",
      "Individual",
    );
    await sendEmail({
      to: form.email.trim(),
      subject,
      body,
      email_type: "registration_confirmation",
      user_id: identity.userId ?? undefined,
    });
    setTimeout(() => navigate("/dashboard"), 1500);
  };

  const handleEinLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.ein.trim()) {
      setError("Please enter your company EIN.");
      return;
    }
    setStatus("submitting");
    setError(null);
    const result = await lookupCompanyByEin(form.ein.trim());
    if (result.error) {
      setError(result.error);
      setStatus("error");
      return;
    }
    setCompanyLookup(result);
    if (result.exists) {
      setStep("company-found");
    } else {
      setStep("company-new");
    }
    setStatus("idle");
  };

  const handleRequestJoin = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError("Please enter your name and email first.");
      return;
    }
    setStatus("submitting");
    setError(null);
    const fullName = `${form.firstName} ${form.lastName}`.trim();
    const result = await requestCompanyJoin(form.ein.trim(), form.email.trim(), fullName);
    if (result.error || !result.verificationCode) {
      setError(result.error ?? "Failed to send verification code.");
      setStatus("error");
      return;
    }
    setJoinCodeSent(true);
    setStep("join-code");
    setStatus("idle");
  };

  const handleNewCompanyRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.companyName.trim() || !form.departmentName.trim()) {
      setError("Company name and department name are required.");
      return;
    }
    if (!form.email.trim() || !form.password) {
      setError("Email and password are required.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setStatus("submitting");
    setError(null);
    const { identity, error: err } = await registerNewCompany({
      email: form.email.trim(),
      password: form.password,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      companyName: form.companyName.trim(),
      ein: form.ein.trim(),
      companyType: form.companyType,
      departmentName: form.departmentName.trim(),
    });
    if (err || !identity) {
      setError(err ?? "Registration failed.");
      setStatus("error");
      return;
    }
    setIdentity(identity);

    // Store logo URL if uploaded
    if (logoPreview && identity.organizationId) {
      await supabase
        .from("sentinel_organizations")
        .update({ logo_url: logoPreview })
        .eq("id", identity.organizationId);
    }

    // Save industry preferences
    if (selectedIndustries.length > 0 && identity.organizationId) {
      await updateOrgIndustryPreferences(identity.organizationId, selectedIndustries);
    }

    // Send confirmation email
    const { subject, body } = buildRegistrationConfirmationEmail(
      [form.firstName, form.lastName].filter(Boolean).join(" "),
      form.companyName,
      form.departmentName,
    );
    await sendEmail({
      to: form.email.trim(),
      subject,
      body,
      email_type: "registration_confirmation",
      user_id: identity.userId ?? undefined,
    });

    setStatus("success");
    setTimeout(() => navigate("/dashboard"), 1500);
  };

  const handleCompleteJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.verificationCode.trim()) {
      setError("Please enter the verification code.");
      return;
    }
    if (!form.password) {
      setError("Please set a password.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (!form.isNewDepartment && !form.existingDepartmentName.trim()) {
      setError("Please select or enter a department name.");
      return;
    }
    setStatus("submitting");
    setError(null);
    const deptName = form.isNewDepartment ? form.departmentName.trim() : form.existingDepartmentName.trim();
    const { identity, error: err } = await completeCompanyJoin({
      email: form.email.trim(),
      password: form.password,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      ein: form.ein.trim(),
      verificationCode: form.verificationCode.trim(),
      departmentName: deptName,
      isNewDepartment: form.isNewDepartment,
    });
    if (err || !identity) {
      setError(err ?? "Registration failed.");
      setStatus("error");
      return;
    }
    setIdentity(identity);
    setStatus("success");
    setTimeout(() => navigate("/dashboard"), 1500);
  };

  const stepLabels: Record<Step, string> = {
    choose: "Choose Account Type",
    ein: "Step 1 of 3",
    "company-new": "Step 2 of 3",
    "company-found": "Step 2 of 3",
    "join-code": "Step 2 of 3",
    personal: "Step 3 of 3",
    individual: "Individual Registration",
  };

  return (
    <>
      <SEO
        title="Register — Sentinel"
        description="Register on the Sentinel Certification Platform as an individual or company."
        canonical="/register"
      />
      <PageHero
        eyebrow={t("register.page.eyebrow")}
        title="Create Your Sentinel Account"
        description="Register as an individual to get certified, or set up a company account to manage your team's training."
        bgVariant="steel"
        bgImage="https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      <Section>
        <div className="register-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-6)" }}>
          {/* ── Left column: form ── */}
          <div>
            <button
              onClick={() => navigate(-1)}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-muted)",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: "var(--space-3)",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.375rem",
              }}
            >
              &larr; Previous Page
            </button>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
              <h3 style={{ marginBottom: 0 }}>{stepLabels[step]}</h3>
              {step !== "choose" && step !== "individual" && (
                <button
                  onClick={() => { setStep("choose"); setError(null); setJoinCodeSent(false); }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--accent-500)",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  &larr; Start Over
                </button>
              )}
            </div>

            {status === "success" ? (
              <div
                className="card"
                style={{
                  borderColor: "var(--accent-400)",
                  background: "var(--accent-50)",
                  textAlign: "center",
                  padding: "var(--space-6)",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>&#10003;</div>
                <h3 style={{ marginBottom: "0.5rem" }}>{t("register.account.success.title")}</h3>
                <p className="text-muted" style={{ marginBottom: "1rem" }}>
                  {t("register.account.success.body")}
                </p>
                <CTA to="/dashboard" variant="primary">Go to Dashboard</CTA>
              </div>
            ) : (
              <>
                {/* ── Step 0: Choose account type ── */}
                {step === "choose" && (
                  <div>
                    <p className="text-muted" style={{ fontSize: "0.9375rem", marginBottom: "1.5rem" }}>
                      Choose how you want to register. You can get certified as an individual, or set up a company account to manage your team's certifications.
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                      <button
                        onClick={() => { setAccountType("individual"); setStep("individual"); }}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "1rem",
                          padding: "1.25rem",
                          borderRadius: "var(--radius-lg)",
                          border: "2px solid var(--border)",
                          background: "var(--bg)",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.15s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--accent-500)"; e.currentTarget.style.background = "var(--accent-50)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--bg)"; }}
                      >
                        <div style={{ flexShrink: 0, width: "2.5rem", height: "2.5rem", borderRadius: "50%", background: "var(--accent-100)", color: "var(--accent-600)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <User size={20} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: "0.25rem" }}>Individual Account</div>
                          <div className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>
                            Get certified as an individual professional. Browse, purchase, and complete certification courses at your own pace.
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => { setAccountType("company"); setStep("ein"); }}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "1rem",
                          padding: "1.25rem",
                          borderRadius: "var(--radius-lg)",
                          border: "2px solid var(--border)",
                          background: "var(--bg)",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.15s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--accent-500)"; e.currentTarget.style.background = "var(--accent-50)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--bg)"; }}
                      >
                        <div style={{ flexShrink: 0, width: "2.5rem", height: "2.5rem", borderRadius: "50%", background: "var(--primary-100)", color: "var(--primary-700)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Building2 size={20} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: "0.25rem" }}>Company Account</div>
                          <div className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>
                            Set up your company with a custom portal. Manage employees, assign courses, track progress, and get branded certificates.
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Individual registration ── */}
                {step === "individual" && (
                  <form onSubmit={handleIndividualRegister}>
                    <div className="card" style={{ padding: "var(--space-3)", background: "var(--accent-50)", marginBottom: "1rem" }}>
                      <p style={{ fontSize: "0.875rem", margin: 0 }}>
                        <strong>Individual Account.</strong> You'll get access to the certification catalog and your personal dashboard.
                      </p>
                    </div>

                    <PersonalInfoFields form={form} update={update} showPassword={showPassword} setShowPassword={setShowPassword} />

                    {/* Industry selection for individual users */}
                    <div className="form-group">
                      <label className="form-label">Industry (select one or more)</label>
                      <p className="text-muted" style={{ fontSize: "0.8125rem", marginTop: "0.375rem", marginBottom: "0.5rem" }}>
                        This pre-filters your certification catalog to show courses relevant to your industry. You can add more industries later.
                      </p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                        {INDUSTRIES.map((ind) => {
                          const selected = selectedIndustries.includes(ind.slug);
                          return (
                            <button
                              key={ind.slug}
                              type="button"
                              onClick={() => {
                                setSelectedIndustries((prev) =>
                                  selected ? prev.filter((s) => s !== ind.slug) : [...prev, ind.slug],
                                );
                              }}
                              style={{
                                padding: "0.375rem 0.75rem",
                                fontSize: "0.8125rem",
                                fontWeight: 600,
                                borderRadius: "var(--radius-full)",
                                border: selected ? "1px solid var(--accent-500)" : "1px solid var(--border)",
                                background: selected ? "var(--accent-50)" : "transparent",
                                color: selected ? "var(--accent-700)" : "var(--text-muted)",
                                cursor: "pointer",
                                transition: "all var(--transition-fast)",
                              }}
                            >
                              {ind.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {error && <p className="form-error">{error}</p>}
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg btn-block"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? "Registering..." : "Create My Account"}
                    </button>
                  </form>
                )}

                {/* ── Step 1: EIN lookup ── */}
                {step === "ein" && (
                  <form onSubmit={handleEinLookup}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="ein">Company EIN / Tax ID</label>
                      <input
                        id="ein"
                        className="form-input"
                        value={form.ein}
                        onChange={(e) => update("ein", e.target.value)}
                        placeholder="e.g. 12-3456789"
                      />
                      <p className="text-muted" style={{ fontSize: "0.8125rem", marginTop: "0.375rem" }}>
                        We check if your company is already registered. If not, you'll create it. If yes, you'll join it with a verification code.
                      </p>
                    </div>
                    {error && <p className="form-error">{error}</p>}
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg btn-block"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? "Checking..." : "Look Up Company"}
                    </button>
                  </form>
                )}

                {/* ── Step 2a: New company ── */}
                {step === "company-new" && (
                  <form onSubmit={handleNewCompanyRegister}>
                    <div className="card" style={{ padding: "var(--space-3)", background: "var(--accent-50)", marginBottom: "1rem" }}>
                      <p style={{ fontSize: "0.875rem", margin: 0 }}>
                        <strong>No company found</strong> with EIN {form.ein}. You'll register as the company originator (admin) and create the first department.
                      </p>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="companyName">Company Name</label>
                      <input
                        id="companyName"
                        className="form-input"
                        value={form.companyName}
                        onChange={(e) => update("companyName", e.target.value)}
                        placeholder="Acme Safety Corp"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="companyType">Company Type</label>
                      <select
                        id="companyType"
                        className="form-input"
                        value={form.companyType}
                        onChange={(e) => update("companyType", e.target.value)}
                      >
                        {COMPANY_TYPES.map((ct) => (
                          <option key={ct.value} value={ct.value}>{ct.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Industry selection */}
                    <div className="form-group">
                      <label className="form-label">Industry (select one or more)</label>
                      <p className="text-muted" style={{ fontSize: "0.8125rem", marginTop: "0.375rem", marginBottom: "0.5rem" }}>
                        This pre-filters your certification catalog to show courses relevant to your industry. You can add more industries later.
                      </p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                        {INDUSTRIES.map((ind) => {
                          const selected = selectedIndustries.includes(ind.slug);
                          return (
                            <button
                              key={ind.slug}
                              type="button"
                              onClick={() => {
                                setSelectedIndustries((prev) =>
                                  selected ? prev.filter((s) => s !== ind.slug) : [...prev, ind.slug],
                                );
                              }}
                              style={{
                                padding: "0.375rem 0.75rem",
                                fontSize: "0.8125rem",
                                fontWeight: 600,
                                borderRadius: "var(--radius-full)",
                                border: selected ? "1px solid var(--accent-500)" : "1px solid var(--border)",
                                background: selected ? "var(--accent-50)" : "transparent",
                                color: selected ? "var(--accent-700)" : "var(--text-muted)",
                                cursor: "pointer",
                                transition: "all var(--transition-fast)",
                              }}
                            >
                              {ind.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Company logo upload */}
                    <div className="form-group">
                      <label className="form-label">Company Logo (optional)</label>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" style={{ maxWidth: "80px", maxHeight: "80px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)" }} />
                        ) : (
                          <div style={{ width: "80px", height: "80px", borderRadius: "var(--radius-md)", border: "2px dashed var(--border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
                            <Building2 size={20} />
                          </div>
                        )}
                        <label className="btn btn-secondary" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.375rem" }}>
                          <Upload size={14} />
                          Upload Logo
                          <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleLogoChange} style={{ display: "none" }} />
                        </label>
                      </div>
                      <p className="text-muted" style={{ fontSize: "0.8125rem", marginTop: "0.375rem" }}>
                        Your logo appears on customized certificates and your company portal.
                      </p>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="departmentName">Your Department Name</label>
                      <input
                        id="departmentName"
                        className="form-input"
                        value={form.departmentName}
                        onChange={(e) => update("departmentName", e.target.value)}
                        placeholder="e.g. Operations, Safety Training, HR"
                      />
                      <p className="text-muted" style={{ fontSize: "0.8125rem", marginTop: "0.375rem" }}>
                        You can add more departments later. Each department manages its own budget.
                      </p>
                    </div>

                    <PersonalInfoFields form={form} update={update} showPassword={showPassword} setShowPassword={setShowPassword} />

                    {error && <p className="form-error">{error}</p>}
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg btn-block"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? "Registering..." : "Register Company & Continue"}
                    </button>
                  </form>
                )}

                {/* ── Step 2b: Existing company found ── */}
                {step === "company-found" && companyLookup?.exists && (
                  <div>
                    <div className="card" style={{ padding: "var(--space-3)", background: "var(--accent-50)", marginBottom: "1rem" }}>
                      <p style={{ fontSize: "0.875rem", margin: "0 0 0.5rem 0" }}>
                        <strong>{companyLookup.organizationName}</strong> is already registered.
                      </p>
                      <p className="text-muted" style={{ fontSize: "0.8125rem", margin: 0 }}>
                        The company originator{companyLookup.originatorName ? ` (${companyLookup.originatorName})` : ""} will receive a verification code to confirm you belong to this organization.
                      </p>
                    </div>

                    <PersonalInfoFields form={form} update={update} showPassword={showPassword} setShowPassword={setShowPassword} />

                    {error && <p className="form-error">{error}</p>}
                    <button
                      onClick={handleRequestJoin}
                      className="btn btn-primary btn-lg btn-block"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? "Sending..." : "Request Verification Code"}
                    </button>
                  </div>
                )}

                {/* ── Step 3: Join code + department ── */}
                {step === "join-code" && joinCodeSent && (
                  <form onSubmit={handleCompleteJoin}>
                    <div className="card" style={{ padding: "var(--space-3)", background: "var(--accent-50)", marginBottom: "1rem" }}>
                      <p style={{ fontSize: "0.875rem", margin: "0 0 0.5rem 0" }}>
                        <strong>Verification code sent.</strong>
                      </p>
                      <p className="text-muted" style={{ fontSize: "0.8125rem", margin: 0 }}>
                        {companyLookup?.originatorEmail
                          ? `The code was sent to ${companyLookup.originatorEmail}. Contact them to get your code.`
                          : "Contact your company administrator to get your code."}
                      </p>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="verificationCode">Verification Code</label>
                      <input
                        id="verificationCode"
                        className="form-input"
                        value={form.verificationCode}
                        onChange={(e) => update("verificationCode", e.target.value)}
                        placeholder="6-digit code"
                        maxLength={6}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.75rem" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.875rem" }}>
                          <input
                            type="radio"
                            name="deptMode"
                            checked={!form.isNewDepartment}
                            onChange={() => update("isNewDepartment", false)}
                          />
                          Join existing
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.875rem" }}>
                          <input
                            type="radio"
                            name="deptMode"
                            checked={form.isNewDepartment}
                            onChange={() => update("isNewDepartment", true)}
                          />
                          Create new
                        </label>
                      </div>
                      {form.isNewDepartment ? (
                        <input
                          className="form-input"
                          value={form.departmentName}
                          onChange={(e) => update("departmentName", e.target.value)}
                          placeholder="New department name"
                        />
                      ) : (
                        <input
                          className="form-input"
                          value={form.existingDepartmentName}
                          onChange={(e) => update("existingDepartmentName", e.target.value)}
                          placeholder="Enter existing department name"
                        />
                      )}
                      <p className="text-muted" style={{ fontSize: "0.8125rem", marginTop: "0.375rem" }}>
                        Each department has its own login and budget, but training is tracked across the whole company.
                      </p>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="joinPassword">Set Password</label>
                      <div style={{ position: "relative" }}>
                        <input
                          id="joinPassword"
                          type={showPassword ? "text" : "password"}
                          className="form-input"
                          value={form.password}
                          onChange={(e) => update("password", e.target.value)}
                          placeholder="At least 6 characters"
                          style={{ paddingRight: "2.5rem" }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: "absolute",
                            right: "0.625rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--text-muted)",
                            padding: 0,
                          }}
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    {error && <p className="form-error">{error}</p>}
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg btn-block"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? "Completing..." : "Complete Registration"}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>

          {/* ── Right column: info panel ── */}
          <div>
            <h3 style={{ marginBottom: "1rem" }}>How Registration Works</h3>
            <p className="text-muted" style={{ marginBottom: "1.5rem", fontSize: "0.9375rem" }}>
              {accountType === "individual"
                ? "Register as an individual to access the full Sentinel certification catalog. Purchase courses, complete training, and earn your certifications at your own pace."
                : "Sentinel uses your company's EIN to uniquely identify it. Each department within the company gets its own login and budget, but all training records roll up to the company level on the backend."}
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {accountType === "individual" ? (
                <>
                  <InfoCard
                    number="1"
                    title="Create Your Account"
                    description="Enter your name, email, and password to get instant access to the platform."
                  />
                  <InfoCard
                    number="2"
                    title="Browse Certifications"
                    description="Explore certifications across 10 industries and 4 levels — from Professional to Executive."
                  />
                  <InfoCard
                    number="3"
                    title="Get Certified"
                    description="Complete modules, pass quizzes, finish capstone simulations, and earn your Sentinel credential."
                  />
                </>
              ) : (
                <>
                  <InfoCard
                    number="1"
                    title="Enter Your EIN"
                    description="We check if your company is already in the system. This uniquely identifies your organization."
                  />
                  <InfoCard
                    number="2"
                    title="Register or Join"
                    description="New company? You become the originator and create the first department. Existing company? A verification code is sent to the originator to confirm you belong."
                  />
                  <InfoCard
                    number="3"
                    title="Set Up Your Department"
                    description="Choose an existing department or create a new one. Each department manages its own training budget, but the company sees everything."
                  />
                </>
              )}
            </div>

            {accountType === "company" && (
              <div
                className="card"
                style={{
                  marginTop: "1.5rem",
                  padding: "var(--space-4)",
                  background: "var(--neutral-50)",
                  border: "1px solid var(--border)",
                }}
              >
                <h4 style={{ fontSize: "0.875rem", fontWeight: 700, marginBottom: "0.5rem" }}>
                  Company Portal Benefits
                </h4>
                <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5, margin: 0 }}>
                  Get a custom portal link (e.g., sentinel-architecture.com/portal/yourcompany) with your company logo, employee management, course assignment, and branded certificates.
                </p>
              </div>
            )}
          </div>
        </div>
        <style>{`
          @media (max-width: 768px) {
            .register-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </Section>
    </>
  );
}

function PersonalInfoFields({
  form,
  update,
  showPassword,
  setShowPassword,
}: {
  form: { firstName: string; lastName: string; email: string; password: string };
  update: (field: string, value: string | boolean) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
}) {
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-2)", marginTop: "1rem" }}>
        <div className="form-group">
          <label className="form-label" htmlFor="firstName">First Name</label>
          <input
            id="firstName"
            className="form-input"
            value={form.firstName}
            onChange={(e) => update("firstName", e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="lastName">Last Name</label>
          <input
            id="lastName"
            className="form-input"
            value={form.lastName}
            onChange={(e) => update("lastName", e.target.value)}
          />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          className="form-input"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="you@example.com"
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="password">Password</label>
        <div style={{ position: "relative" }}>
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            className="form-input"
            value={form.password}
            onChange={(e) => update("password", e.target.value)}
            placeholder="At least 6 characters"
            style={{ paddingRight: "2.5rem" }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: "absolute",
              right: "0.625rem",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--text-muted)",
              padding: 0,
            }}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>
    </>
  );
}

function InfoCard({ number, title, description }: { number: string; title: string; description: string }) {
  return (
    <div
      style={{
        display: "flex",
        gap: "1rem",
        padding: "1rem 1.25rem",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border)",
        background: "var(--bg)",
        transition: "all var(--transition-fast)",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: "2rem",
          height: "2rem",
          borderRadius: "50%",
          background: "var(--accent-500)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: "0.875rem",
        }}
      >
        {number}
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: "0.9375rem", marginBottom: "0.25rem" }}>{title}</div>
        <div className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>{description}</div>
      </div>
    </div>
  );
}
